import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import {AnalysisService} from '../../services/analysis.service';
import {BarCardComponent} from '@/app/shared/components/bar-card/bar-card.component';
import {MultiDonutCardComponent} from '@/app/shared/components/multi-donut-card/multi-donut-card.component';
import {AnalysisDemographicFilters} from '../../infrastructure/analysis.api.types';
import {Risk} from '@/app/utils/risk-colors';
import {
  BarCardVM,
  buildBarCard,
  buildDonutCard,
  ChartCardVM,
  DemographicKey,
  DonutCardVM,
  DonutMini,
  FACET_LABELS,
  processCards,
  readFilterValueForFacet,
  safeCategory
} from "../../utils/report-chart.utils";
import {EmptyStateComponent} from '../empty-state/empty-state.component';

type ViewModel = {
  donutCards: DonutCardVM[];
  barCards: BarCardVM[];
  isEmpty: boolean;
  loading: boolean;
};

const deepEqual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

@Component({
  selector: 'app-category-scores-breakdown',
  standalone: true,
  imports: [CommonModule, BarCardComponent, MultiDonutCardComponent, EmptyStateComponent],
  templateUrl: './category-scores-breakdown.component.html',
  styleUrls: ['./final-assessments-breakdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryScoresBreakdownComponent {
  @Input() showEmpty = false;
  @Input() showLoading = false;
  private analysis = inject(AnalysisService);
  private readonly facetLabels = FACET_LABELS;

  private campaignId$ = new BehaviorSubject<string | null>(null);
  private guideId$ = new BehaviorSubject<string | null>(null);
  private facets$ = new BehaviorSubject<string[]>([]);
  private filters$ = new BehaviorSubject<Partial<AnalysisDemographicFilters>>({});

  public readonly vm$: Observable<ViewModel> = combineLatest({
    camp: this.campaignId$.pipe(distinctUntilChanged()),
    guide: this.guideId$.pipe(distinctUntilChanged()),
    facets: this.facets$.pipe(distinctUntilChanged<string[]>((prev, curr) => deepEqual(prev, curr))),
    filters: this.filters$.pipe(
      distinctUntilChanged<Partial<AnalysisDemographicFilters>>((prev, curr) => deepEqual(prev, curr))
    ),
  }).pipe(
    filter(({ camp, guide }) => !!camp && !!guide),
    switchMap(({ camp, guide, facets, filters }) =>
      this.analysis
        .getCategoryScores({
          campaign: camp!,
          guideId: guide!,
          groupBy: facets,
          ...(filters ?? {}),
        })
        .pipe(
          distinctUntilChanged<any[]>((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          map((rows: any[]) => {
            const cards = this.buildCards(rows as any[], facets as DemographicKey[], filters);
            const { donutCards, barCards, isEmpty } = processCards(cards, rows);
            return { donutCards, barCards, isEmpty, loading: false } as ViewModel;
          }),
          startWith({ donutCards: [], barCards: [], isEmpty: false, loading: true } as ViewModel)
        )
    ),
    startWith({ donutCards: [], barCards: [], isEmpty: false, loading: false } as ViewModel),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  @Input({ required: true }) set campaignId(v: string) {
    this.campaignId$.next(v ?? null);
  }

  @Input() set guideId(v: string) {
    this.guideId$.next(v ?? null);
  }

  @Input() set activeFacets(v: string[]) {
    this.facets$.next(Array.isArray(v) ? v : []);
  }

  @Input() set filters(v: Partial<AnalysisDemographicFilters>) {
    this.filters$.next(v ?? {});
  }

  trackByCard = (_: number, c: ChartCardVM) => c.title;

  private _rowRisk = (r: any): Risk | null => (r.rating as Risk) || null;

  private buildCards(
    rows: any[],
    facets: DemographicKey[],
    filters: Partial<AnalysisDemographicFilters>,
  ): ChartCardVM[] {
    if (!Array.isArray(rows)) return [];

    const byCategory = new Map<string, any[]>();
    for (const r of rows) {
      const category = safeCategory(r.categoryName);
      if (!category) continue;
      const arr = byCategory.get(category) ?? [];
      arr.push(r);
      byCategory.set(category, arr);
    }

    const out: ChartCardVM[] = [];

    for (const [categoryName, categoryRows] of byCategory.entries()) {
      // Si no hay facets, crear gráfica sin filtros demográficos
      if (facets.length === 0) {
        const title = categoryName;
        const baseCard = { facet: 'categoryName' as const, title };
        const donutBuckets = new Map([['', categoryRows]]);
        const seriesFn = (present: {risk: Risk, count: number}[], minis: DonutMini[]) => {
          if (minis.length === 1 && minis[0].title === '') {
            return present.map(x => ({ name: x.risk, value: x.count }));
          }
          return present.map(x => ({ name: `${x.count} con riesgo ${x.risk.toLowerCase()}`, value: x.count }));
        };
        const totalHintFn = (subTotal: number) => `Total: ${subTotal} cuestionarios respondidos.`;
        const card = buildDonutCard(baseCard, donutBuckets, this._rowRisk, seriesFn, totalHintFn);
        out.push(card);
        continue;
      }

      // Procesar cada facet individualmente
      for (const facet of facets) {
        const byFacetValue = new Map<string, any[]>();
        for (const r of categoryRows) {
          const facetValue = safeCategory((r as any)[facet]);
          if (!facetValue) continue;
          const arr = byFacetValue.get(facetValue) ?? [];
          arr.push(r);
          byFacetValue.set(facetValue, arr);
        }

        const useBarChart = byFacetValue.size > 1;
        const totalHintFn = (subTotal: number) => `Total: ${subTotal} cuestionarios respondidos.`;
        const facetLabel = this.facetLabels.get(facet) ?? facet;

        if (useBarChart) {
          const title = `${categoryName} por ${facetLabel}`;
          const baseCard = { facet: 'categoryName' as const, title };
          const card = buildBarCard(baseCard, categoryRows, byFacetValue, this._rowRisk, totalHintFn);
          out.push(card);
        } else {
          const filterValue = readFilterValueForFacet(facet, filters);
          let title = `${categoryName}${filterValue ? `: ${filterValue}` : ''}`;

          if (byFacetValue.size === 1) {
            const singleBucketKey = [...byFacetValue.keys()][0];
            title = `${categoryName} (${facetLabel}: ${singleBucketKey})`;
          }

          const baseCard = { facet: 'categoryName' as const, title };
          const donutBuckets = byFacetValue.size > 0 ? byFacetValue : new Map([['', categoryRows]]);
          const seriesFn = (present: {risk: Risk, count: number}[], minis: DonutMini[]) => {
            if (minis.length === 1 && minis[0].title === '') {
              return present.map(x => ({ name: x.risk, value: x.count }));
            }
            return present.map(x => ({ name: `${x.count} con riesgo ${x.risk.toLowerCase()}`, value: x.count }));
          };

          const card = buildDonutCard(baseCard, donutBuckets, this._rowRisk, seriesFn, totalHintFn);
          out.push(card);
        }
      }
    }
    return out;
  }
}
